import { useEffect, useState } from "react";
import { Loader2, Plus, Tag } from "lucide-react";
import { http, errorText } from "@/lib/adminApi";

const field = "w-full rounded-xl border border-white/15 bg-[var(--ink)] px-4 py-3 text-white placeholder:text-slate-500 focus:border-[var(--teal)] focus:outline-none";
const EMPTY = { code: "", kind: "percent", value: "", max_redemptions: "", expires_on: "" };

const offText = (p) => (p.percent_off ? `${p.percent_off}% off` : `$${p.amount_off} off`);

const PromoForm = ({ onCreated }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: k === "code" ? e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    try {
      const { data } = await http.post("/admin/promos", {
        code: f.code, kind: f.kind, value: Number(f.value),
        max_redemptions: f.max_redemptions ? Number(f.max_redemptions) : null, expires_on: f.expires_on || null,
      });
      onCreated(data);
      setF(EMPTY);
    } catch (err) { setMsg(errorText(err)); }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-[var(--navy)] p-5 sm:p-6" data-testid="promo-form">
      <p className="font-semibold text-white">New promo code</p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <label className="text-sm text-slate-300">Code
          <input required className={`${field} mt-1.5 font-mono-d tracking-widest`} placeholder="LAUNCH20" value={f.code} onChange={set("code")} data-testid="promo-code-input" />
        </label>
        <div className="text-sm text-slate-300">Discount
          <div className="mt-1.5 flex gap-2">
            <select className={`${field} w-28 shrink-0`} value={f.kind} onChange={set("kind")} data-testid="promo-kind-select">
              <option value="percent">% off</option>
              <option value="amount">$ off</option>
            </select>
            <input required type="number" min="1" step="1" className={field} placeholder={f.kind === "percent" ? "20" : "15"} value={f.value} onChange={set("value")} data-testid="promo-value-input" />
          </div>
        </div>
        <label className="text-sm text-slate-300">Max uses (optional)
          <input type="number" min="1" className={`${field} mt-1.5`} placeholder="Unlimited" value={f.max_redemptions} onChange={set("max_redemptions")} data-testid="promo-max-input" />
        </label>
        <label className="text-sm text-slate-300">Expires on (optional)
          <input type="date" className={`${field} mt-1.5 [color-scheme:dark]`} value={f.expires_on} onChange={set("expires_on")} data-testid="promo-expiry-input" />
        </label>
      </div>
      {msg && <p className="mt-3 text-sm text-[var(--amber)]" role="alert" data-testid="promo-error">{msg}</p>}
      <button disabled={busy} className="mt-5 inline-flex items-center gap-2 rounded-full bg-[var(--teal)] px-5 py-2.5 text-sm font-semibold text-[var(--ink)] disabled:opacity-60" data-testid="promo-create-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create code
      </button>
    </form>
  );
};

export const PromosPanel = () => {
  const [promos, setPromos] = useState(null);
  const [featured, setFeatured] = useState(null);
  useEffect(() => {
    http.get("/admin/promos").then((r) => setPromos(Array.isArray(r.data?.promos) ? r.data.promos : [])).catch(() => setPromos([]));
    http.get("/admin/promos/banner").then((r) => setFeatured(r.data?.promo_id || null)).catch(() => setFeatured(null));
  }, []);

  const feature = async (id) => {
    const prev = featured;
    setFeatured(id);
    try { await http.put("/admin/promos/banner", { promo_id: id }); } catch { setFeatured(prev); }
  };

  const toggle = async (p) => {
    const active = !p.active;
    setPromos((l) => l.map((x) => (x.id === p.id ? { ...x, active } : x)));
    try { await http.patch(`/admin/promos/${p.id}`, { active }); }
    catch { setPromos((l) => l.map((x) => (x.id === p.id ? { ...x, active: p.active } : x))); }
  };

  return (
    <div className="mt-10 space-y-6" data-testid="admin-promos-panel">
      <p className="text-sm text-slate-300">Customers type the code on the payment page. Each code applies once per order. Pick one active code to announce in a slim banner at the top of your site; it disappears automatically when the code ends or is turned off.</p>
      <PromoForm onCreated={(p) => setPromos((l) => [p, ...(l || [])])} />
      {!promos && <Loader2 className="mx-auto h-6 w-6 animate-spin text-[var(--teal)]" />}
      {promos?.length === 0 && <p className="text-slate-300" data-testid="promos-empty">No codes yet.</p>}
      <ul className="space-y-3">
        {(promos || []).map((p) => (
          <li key={p.id} className={`flex flex-wrap items-center justify-between gap-3 rounded-2xl border p-5 ${p.active ? "border-[var(--teal)]/40 bg-[var(--navy)]" : "border-white/10 bg-[var(--navy)]/50 opacity-70"}`} data-testid={`promo-${p.code}`}>
            <div>
              <p className="font-mono-d flex items-center gap-2 text-lg tracking-widest text-white"><Tag className="h-4 w-4 text-[var(--teal)]" />{p.code}</p>
              <p className="mt-1 text-sm text-slate-300">
                {offText(p)} · used {p.times_redeemed}{p.max_redemptions ? ` of ${p.max_redemptions}` : ""} times
                {p.expires_at ? ` · ends ${new Date(p.expires_at * 1000).toLocaleDateString()}` : ""}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {p.active && (
                <button onClick={() => feature(featured === p.id ? null : p.id)} data-testid={`promo-feature-${p.code}`}
                  className={`rounded-full px-4 py-2 text-sm font-semibold ${featured === p.id ? "bg-[var(--amber)] text-[var(--ink)]" : "border border-white/20 text-slate-200 hover:border-[var(--teal)]"}`}>
                  {featured === p.id ? "In site banner · remove" : "Show in site banner"}
                </button>
              )}
              <button onClick={() => toggle(p)} data-testid={`promo-toggle-${p.code}`}
                className={`rounded-full px-4 py-2 text-sm font-semibold ${p.active ? "border border-white/20 text-slate-200 hover:border-[var(--amber)] hover:text-[var(--amber)]" : "bg-[var(--teal)] text-[var(--ink)]"}`}>
                {p.active ? "Turn off" : "Turn back on"}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};
